'use client';

import React, { PropsWithChildren, useState, useTransition } from 'react';
import Button, { ButtonSize } from '../Button/Button';
import { FormikValues, useFormik } from 'formik';
import FormikCheckbox from '../Checkbox/FormikCheckbox';
import FormikInputField from '../InputField/FormikInputField';
import FormikSelect from '../Select/FormikSelect';
import { useFormHelpers } from '@/src/lib/utils/form-helpers';
import { Register } from '@/src/types/register';
import { Salutation } from '@/src/lib/customer/types';
import { GetUser } from '@/src/lib/auth/types';
import { DeserializedStore } from '@/src/lib/store/types';
import { Dictionary } from '@/src/lib/dictionary/types';
import { useNotify } from '@/src/lib/notification/notify';
import { NotificationType } from '@/src/app/components/Notification/types';
import LinkedLabelText from '@/src/app/components/LinkedLabelText/LinkedLabelText';
import { eventBus } from '@/src/lib/core/event/bus';
import { EventBusName } from '@/src/lib/core/event/types';
import { object } from 'yup';
import { getRegisterFormValidationRules } from '@/src/lib/validation/business/get-register-form-validation-rules';
import { useAsyncStartTransition } from '@/src/lib/utils/hooks/use-async-start-transition';
import { isFailure } from '@/src/lib/http/business/server-action-results';
import { getTenantConfigValue } from '@/src/lib/store/business/get-tenants-config-value';
import { getRegionLabelForSelectedCountry } from '@/src/lib/utils/localization.utils';
import { handleException } from '@/src/lib/core/exception/handle-exception';
import { registerFormSubmit } from '@/src/app/components/RegisterModalBlocker/actions';
import { useRouter } from 'next/navigation';
export interface RegisterForm {
  salutation: Salutation;
  firstName: string;
  lastName: string;
  email: string;
  country: string;
  customerNumber: string;
  phone: string;
  company: string;
  taxId: string;
  role: string;
  street: string;
  houseNumber: string;
  addressInformation: string;
  zipCode: string;
  city: string;
  region: string | null;
  acceptedTerms: boolean;
  confirmTaxableEntity: boolean;
}
type RegisterFormProps = PropsWithChildren<{
  dict: Dictionary;
  user: GetUser;
  store: DeserializedStore;
  lang: string;
  onSubmit: typeof registerFormSubmit;
}>;
function RegisterForm({
  dict,
  user,
  store,
  onSubmit,
  lang
}: RegisterFormProps) {
  const titleSelectOptions = [{
    value: Salutation.MR,
    label: dict.userProfile.salutation.mr
  }, {
    value: Salutation.MS,
    label: dict.userProfile.salutation.ms
  }, {
    value: Salutation.NONE,
    label: dict.userProfile.salutation.none
  }];
  const functionSelectOptions = Object.keys(dict.userProfile.roles).map(key => ({
    // @ts-ignore
    value: dict.userProfile.roles[key],
    // @ts-ignore
    label: dict.userProfile.roles[key]
  }));
  const [submitLoading, setSubmitLoading] = useState(false);
  const {
    flashNotification
  } = useNotify();
  const asyncStartTransition = useAsyncStartTransition();
  const [_, startTransition] = useTransition();
  const router = useRouter();
  const {
    countryOptions,
    handleErrors
  } = useFormHelpers(dict, store.countries);
  const validationSchema = getRegisterFormValidationRules(store.id, dict);
  const formikValidationSchemaShape = object().shape({
    ...validationSchema
  });
  const formikValidationSchemaObject = object(validationSchema);
  const formik = useFormik<FormikValues & RegisterForm>({
    initialValues: {
      salutation: Salutation.NONE,
      firstName: '',
      lastName: '',
      email: user?.user?.email ?? '',
      country: '',
      region: null,
      customerNumber: '',
      phone: '',
      company: '',
      taxId: '',
      role: '',
      street: '',
      houseNumber: '',
      addressInformation: '',
      zipCode: '',
      city: '',
      acceptedTerms: false,
      confirmTaxableEntity: false
    },
    onSubmit: async (values: RegisterForm, {
      setErrors
    }) => {
      try {
        setSubmitLoading(true);
        await handleSubmit(values);
      } catch (e: unknown) {
        handleException(e);
        const {
          triggerPageRefreshAndLogout
        } = handleErrors(e, false, setErrors);
        if (triggerPageRefreshAndLogout) {
          startTransition(() => {
            return router.refresh();
          });
        }
      } finally {
        setSubmitLoading(false);
      }
    },
    validateOnMount: true,
    validationSchema: formikValidationSchemaShape
  });
  const handleSubmit = async (values_0: RegisterForm) => {
    const data: Register.RequestPayload = {
      salutation: values_0.salutation,
      firstName: values_0.firstName,
      lastName: values_0.lastName,
      email: values_0.email,
      country: values_0.country || '',
      region: values_0.region,
      customerNumber: values_0.customerNumber,
      phone: values_0.phone,
      company: values_0.company,
      taxId: values_0.taxId,
      role: values_0.role || '',
      street: values_0.street,
      houseNumber: values_0.houseNumber,
      addressInformation: values_0.addressInformation,
      zipCode: values_0.zipCode,
      city: values_0.city,
      acceptedTerms: values_0.acceptedTerms
    };
    await asyncStartTransition(async () => {
      const response = await onSubmit(data);
      if (isFailure(response)) {
        throw response.error;
      }
      flashNotification(dict.messages.form.registration.succeeded, NotificationType.SUCCESS);
      if (!response.data?.data?.attributes?.isVerified) {
        eventBus.emit(EventBusName.USER_REGISTERED, response.data?.data.attributes);
      }
    });
    eventBus.emit(EventBusName.CLOSE_REGISTER_MODAL_BLOCKER);
  };
  const privacyStatementUrl = (getTenantConfigValue('privacyStatementUrl') as {
    [key: string]: string;
  })[lang];
  const userAgreementUrl = (getTenantConfigValue('userAgreementUrl') as {
    [key: string]: string;
  })[lang];
  const selectedCountry = store.countries.find(country => country.iso2Code === formik.values.country);
  const displayRegion = !!selectedCountry?.regions.length && selectedCountry?.regions.length > 0;
  const regionLabel = getRegionLabelForSelectedCountry(formik.values.country, dict);
  return <>
      <h3 className="display-title-3xl">{dict.userProfile.register.title}</h3>
      <p className="text-base text-gray-500">{dict.userProfile.register.description}</p>
      <form className="grid lg:grid-cols-2 gap-6 mt-8" onSubmit={formik.handleSubmit} data-testid="registration-form">
        <div>
          <FormikSelect options={titleSelectOptions} label={dict.userProfile.userData.userTitle} name="salutation" formikValidationSchema={formikValidationSchemaObject} placeholder={dict.general.selectFieldPlaceholder} formik={formik} testId="salutation" className="w-1/3 min-w-48" />
        </div>
        <div className="hidden lg:block" />
        <FormikInputField label={dict.userProfile.userData.firstName} name="firstName" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.firstName} />
        <FormikInputField label={dict.userProfile.userData.lastName} name="lastName" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.lastName} />
        <FormikInputField label={dict.userProfile.userData.customerNumber} name="customerNumber" formik={formik} placeholder={dict.userProfile.register.placeholders.customerNumber} formikValidationSchema={formikValidationSchemaObject} />
        <FormikInputField label={dict.userProfile.userData.phoneNumber} name="phone" type="tel" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.phoneNumber} />
        <FormikInputField label={dict.userProfile.userData.email} name="email" type="email" formik={formik} readOnly placeholder={dict.userProfile.register.placeholders.emailAddress} formikValidationSchema={formikValidationSchemaObject} />
        <div>
          <FormikSelect options={functionSelectOptions} label={dict.userProfile.userData.function} placeholder={dict.general.selectFieldPlaceholder} name="role" formikValidationSchema={formikValidationSchemaObject} formik={formik} testId="role" />
        </div>
        <FormikInputField label={dict.userProfile.userData.companyName} name="company" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.companyName} />
        <FormikInputField label={dict.userProfile.userData.companyTaxId} name="taxId" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.taxId} />
        <div className="grid grid-cols-3 gap-[10px]">
          <div className="col-span-2">
            <FormikInputField label={dict.userProfile.userData.street} name="street" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.street} />
          </div>
          <div>
            <FormikInputField label={dict.userProfile.userData.houseNumber} name="houseNumber" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.houseNo} />
          </div>
        </div>
        <FormikInputField label={dict.userProfile.userData.additionalAddressInformation} name="addressInformation" formik={formik} formikValidationSchema={formikValidationSchemaObject} placeholder={dict.userProfile.register.placeholders.additionalAddress} />
        <div className="grid grid-cols-3 gap-[10px]">
          <div>
            <FormikInputField label={dict.userProfile.userData.postcode} name="zipCode" formikValidationSchema={formikValidationSchemaObject} formik={formik} placeholder={dict.userProfile.register.placeholders.zip} />
          </div>
          <div className="col-span-2">
            <FormikInputField label={dict.userProfile.userData.city} name="city" formik={formik} formikValidationSchema={formikValidationSchemaObject} placeholder={dict.userProfile.register.placeholders.city} />
          </div>
        </div>
        <div>
          <FormikSelect options={countryOptions} label={dict.userProfile.userData.countryRegion} placeholder={dict.general.selectFieldPlaceholder} name="country" formikValidationSchema={formikValidationSchemaObject} formik={formik} testId="country" />
        </div>
        {displayRegion ? <>
            <div>
              <FormikSelect options={selectedCountry?.regions.map(region => ({
            label: region.name,
            value: region.iso2Code
          }))} placeholder={dict.general.selectFieldPlaceholder} className="lg:col-span-6" label={regionLabel} name="region" formikValidationSchema={formikValidationSchemaObject} formik={formik} testId="region" />
            </div>
            <div />
          </> : null}
        <div className="flex flex-col gap-4">
          <FormikCheckbox label={<LinkedLabelText labelText={dict.userProfile.pleaseAcceptUA} url={`${userAgreementUrl}?showRegisterForm=false`} />} labelClassName="thin" id="acceptedTerms" name="acceptedTerms" onChange={formik.handleChange} checked={formik.values.acceptedTerms} formik={formik} value="true" formikValidationSchema={formikValidationSchemaObject} />
          <FormikCheckbox label={<LinkedLabelText labelText={dict.userProfile.confirmTaxableEntity} url={`${privacyStatementUrl}?showRegisterForm=false`} />} labelClassName="thin" id="confirmTaxableEntity" name="confirmTaxableEntity" onChange={formik.handleChange} checked={formik.values.confirmTaxableEntity} formik={formik} value="true" formikValidationSchema={formikValidationSchemaObject} />
        </div>
        <div className="flex flex-col-reverse items-end justify-end">
          <Button primary priority size={ButtonSize.LARGE} type="submit" loading={submitLoading} disabled={!formik.isValid}>
            {dict.userProfile.registerButton}
          </Button>
        </div>
      </form>
    </>;
}
export default RegisterForm;